import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Avatar,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { AutoAwesome, Delete, Save, ArrowBack, Edit as EditIcon } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'

const providers = [
  { value: 'openai', label: 'OpenAI (GPT)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
]

const modelPlaceholders = {
  openai: 'ex: gpt-4o-mini',
  anthropic: 'ex: claude-sonnet-4-20250514',
}

const AIProviderConfigForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const isEdit = !!id

  const [isLoading, setIsLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [maskedApiKey, setMaskedApiKey] = useState('')
  const [editingApiKey, setEditingApiKey] = useState(false)

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      provider: 'openai',
      api_key: '',
      secondary_key: '',
      default_model: '',
      is_active: true,
    },
  })

  const selectedProvider = watch('provider')

  useEffect(() => {
    if (isEdit) {
      fetchConfig()
    }
  }, [id])

  const fetchConfig = async () => {
    try {
      const res = await api.get(`/ai/provider-configs/${id}/`)
      const data = res.data
      setMaskedApiKey(data.api_key_masked || '')
      reset({
        name: data.name || '',
        provider: data.provider || 'openai',
        api_key: '',
        secondary_key: data.secondary_key || '',
        default_model: data.default_model || '',
        is_active: data.is_active ?? true,
      })
    } catch (err) {
      enqueueSnackbar('Erro ao carregar configuração', { variant: 'error' })
      navigate('/admin/ai-provider-config')
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true)

      const payload = { ...formData }

      // Se editando e o usuário não editou a chave, não envia
      if (isEdit && !editingApiKey) {
        delete payload.api_key
      }

      // Remove campos vazios opcionais
      if (!payload.secondary_key) delete payload.secondary_key
      if (!payload.default_model) delete payload.default_model

      if (isEdit) {
        await api.put(`/ai/provider-configs/${id}/`, payload)
        enqueueSnackbar('Configuração atualizada com sucesso', { variant: 'success' })
      } else {
        await api.post('/ai/provider-configs/', payload)
        enqueueSnackbar('Configuração criada com sucesso', { variant: 'success' })
      }
      navigate('/admin/ai-provider-config')
    } catch (err) {
      const errorMsg = err?.response?.data
      if (errorMsg && typeof errorMsg === 'object') {
        const firstError = Object.values(errorMsg).flat()[0]
        enqueueSnackbar(firstError || 'Erro ao salvar', { variant: 'error' })
      } else {
        enqueueSnackbar('Erro ao salvar configuração', { variant: 'error' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    try {
      setSubmitting(true)
      await api.delete(`/ai/provider-configs/${id}/`)
      enqueueSnackbar('Configuração excluída com sucesso', { variant: 'success' })
      navigate('/admin/ai-provider-config')
    } catch (err) {
      enqueueSnackbar('Erro ao excluir configuração', { variant: 'error' })
    } finally {
      setSubmitting(false)
      setDeleteDialogOpen(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Editar' : 'Nova'} Configuração de IA - Admin</title>
      </Helmet>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={submitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/ai-provider-config')}
            sx={{ mb: 2 }}
          >
            Voltar
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {isEdit ? 'Editar Configuração de IA' : 'Nova Configuração de IA'}
          </Typography>
        </Box>

        <Card>
          {isLoading ? (
            <Skeleton variant="rounded" height={500} animation="pulse" />
          ) : (
            <>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <AutoAwesome />
                  </Avatar>
                }
                title={isEdit ? 'Editar Provedor' : 'Novo Provedor'}
                subheader="Configure as credenciais do provedor de IA"
              />
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={2} rowSpacing={3}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Nome é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Nome"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="provider"
                        control={control}
                        rules={{ required: 'Provider é obrigatório' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            select
                            label="Provider"
                            fullWidth
                            size="small"
                            required
                            error={!!errors.provider}
                            helperText={errors.provider?.message}
                          >
                            {providers.map((p) => (
                              <MenuItem key={p.value} value={p.value}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {isEdit && !editingApiKey && maskedApiKey ? (
                        <TextField
                          label="API Key"
                          value={maskedApiKey}
                          fullWidth
                          size="small"
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingApiKey(true)}
                                  title="Editar chave"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          helperText="Clique no ícone para alterar a chave"
                        />
                      ) : (
                        <Controller
                          name="api_key"
                          control={control}
                          rules={!isEdit ? { required: 'API Key é obrigatória' } : {}}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="API Key"
                              fullWidth
                              size="small"
                              type="password"
                              required={!isEdit}
                              autoFocus={editingApiKey}
                              error={!!errors.api_key}
                              helperText={errors.api_key?.message}
                            />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="secondary_key"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={selectedProvider === 'openai' ? 'Organization ID' : 'Chave Secundária'}
                            fullWidth
                            size="small"
                            helperText="Opcional"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="default_model"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Modelo Padrão"
                            fullWidth
                            size="small"
                            placeholder={modelPlaceholders[selectedProvider] || ''}
                            helperText="Opcional — deixe vazio para usar o modelo padrão do sistema"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color="primary"
                              />
                            }
                            label="Ativo"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    {isEdit ? (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => setDeleteDialogOpen(true)}
                      >
                        Excluir
                      </Button>
                    ) : (
                      <Box />
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                    >
                      {isEdit ? 'Salvar' : 'Criar'}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </Container>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir esta configuração de provedor de IA?
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AIProviderConfigForm
